import { FC } from 'react';
import { Box } from '@mui/material';
import type { Specification } from '../../types';
import { SpecField } from './spec-field';

interface SpecFieldsGridProps {
  specs: Specification[];
  values: Record<string, string>;
  onChange: (specId: string, value: string) => void;
  /**
   * Minimum column width in pixels. Columns fit as many as the container can
   * hold at this width; empty tracks collapse so fewer specs never produce
   * stretched-out single columns.
   */
  minColumnWidth?: number;
  /** MUI spacing units between cells. */
  gap?: number;
}

/**
 * Renders a set of specification inputs in a responsive CSS grid.
 * Columns are sized as `minmax(minColumnWidth, 1fr)` with `auto-fit`, so the
 * grid grows to the widest column count that fits the container and caps at
 * the number of specs (extra tracks collapse when there is nothing to place
 * in them).
 * @param root0
 * @param root0.specs
 * @param root0.values
 * @param root0.onChange
 * @param root0.minColumnWidth
 * @param root0.gap
 */
export const SpecFieldsGrid: FC<SpecFieldsGridProps> = ({
  specs,
  values,
  onChange,
  minColumnWidth = 240,
  gap = 2,
}) => {
  if (specs.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
        gap,
        width: '100%',
      }}
    >
      {specs.map((spec) => (
        <SpecField
          key={spec.id}
          spec={spec}
          value={values[spec.id] || spec.defaultValue || ''}
          onChange={(v) => onChange(spec.id, v)}
        />
      ))}
    </Box>
  );
};
